import { soundVolume, volumeSteps } from "../constants";

// Geluid op het dashboard, ter plekke gemaakt met WebAudio.
// Browsers blokkeren geluid tot de eerste aanraking: listenForUnlock vangt die op, of start
// Chromium met --autoplay-policy=no-user-gesture-required (zie PRESTATIES.md).
// Uitzetten per scherm met ?sound=off of de knop in het animatielab; de keuze blijft bewaard.

const storeKey = "tdSound";

let ctx = null;
let master = null;
let noiseBuffer = null;
let on = null;
const listeners = new Set();

// Keuzes per scherm in localStorage (ook de geluidsset in cues.js).
export function readSetting(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Kiosk-modus zonder opslag: de keuze geldt dan alleen deze sessie.
    return null;
  }
}

export function writeSetting(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Geen opslag: niets aan te doen.
  }
}

// Een keuze uit de URL (?sound=off, ?seintje=oud), die meteen onthouden wordt.
export function readUrlSetting(param, allowed, key) {
  try {
    const asked = new URLSearchParams(window.location.search).get(param);
    if (!asked || !allowed.includes(asked)) return null;
    writeSetting(key, asked);
    return asked;
  } catch {
    return null;
  }
}

function resolve() {
  return (readUrlSetting("sound", ["on", "off"], storeKey) || readSetting(storeKey)) !== "off";
}

export function soundOn() {
  if (on === null) on = resolve();
  return on;
}

export function setSoundOn(next, { remember = true } = {}) {
  on = Boolean(next);
  if (remember) writeSetting(storeKey, on ? "on" : "off");
  if (on) resume();
  listeners.forEach((listener) => listener(on));
}

// Voor het labpaneel, dat meeverandert met de knop.
export function onSoundChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Volume in hele stappen van 0 tot volumeSteps, per scherm onthouden (Shift+pijltjes).
// Bewaard als fractie van 0 tot 1, zodat de keuze klopt als volumeSteps ooit verandert.
const volumeKey = "tdVolumeGain";
let volume = null;
const volumeListeners = new Set();

export function getVolume() {
  if (volume !== null) return volume;
  const saved = Number.parseFloat(readSetting(volumeKey));
  const gain = Number.isNaN(saved) ? soundVolume : Math.min(1, Math.max(0, saved));
  volume = Math.round(gain * volumeSteps);
  return volume;
}

function volumeGain() {
  return getVolume() / volumeSteps;
}

export function setVolume(next) {
  volume = Math.min(volumeSteps, Math.max(0, Math.round(next)));
  writeSetting(volumeKey, String(volumeGain()));
  // Kort glijden, anders klikt een geluid dat net speelt.
  if (ctx && master) master.gain.setTargetAtTime(volumeGain(), ctx.currentTime, 0.03);
  volumeListeners.forEach((listener) => listener(volume));
  return volume;
}

export function onVolumeChange(listener) {
  volumeListeners.add(listener);
  return () => volumeListeners.delete(listener);
}

// Pas bij het eerste geluid gemaakt: bij het laden levert een geblokkeerde context op.
function context() {
  if (ctx) return ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;

  ctx = new Ctx();
  master = ctx.createGain();
  master.gain.value = volumeGain();

  // Begrenzer: vangt alleen de pieken af van geluiden die over elkaar vallen, anders kraakt het.
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -3;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.002;
  limiter.release.value = 0.12;

  master.connect(limiter);
  limiter.connect(ctx.destination);
  return ctx;
}

// Extra volume voor het geluid dat nu klaargezet wordt (zie accented in cues.js).
// Een geluid wordt in één keer gepland, dus alles binnen withAccent hoort erbij.
let accent = 1;

export function withAccent(factor, run) {
  accent = factor;
  try {
    run();
  } finally {
    accent = 1;
  }
}

function resume() {
  const audio = context();
  if (!audio) return false;
  if (audio.state === "suspended") audio.resume().catch(() => {});
  return audio.state === "running";
}

// Of er nu echt iets te horen zou zijn; het lab laat dit zien.
export function audioReady() {
  return soundOn() && Boolean(ctx && ctx.state === "running");
}

// Wacht op de eerste aanraking; geeft een opruimfunctie terug.
export function listenForUnlock() {
  if (typeof window === "undefined") return () => {};

  // Meteen proberen: met de kioskvlag mag het al.
  resume();

  const wake = () => {
    if (resume()) stop();
  };
  const events = ["pointerdown", "keydown", "touchstart"];
  const stop = () => events.forEach((name) => window.removeEventListener(name, wake));

  events.forEach((name) => window.addEventListener(name, wake, { passive: true }));
  return stop;
}

// Eén seconde ruis voor klappen en veegjes.
function noise() {
  const audio = context();
  if (!audio) return null;
  if (noiseBuffer) return noiseBuffer;

  noiseBuffer = audio.createBuffer(1, audio.sampleRate, audio.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  return noiseBuffer;
}

// De context als er nu geluid mag klinken, anders null (en alvast proberen te wekken).
function open() {
  if (!soundOn()) return null;
  const audio = context();
  if (!audio || audio.state !== "running") {
    resume();
    return null;
  }
  return audio;
}

// Eén toon, met to glijdend naar een andere frequentie; uitdoving exponentieel.
export function tone({
  at = 0,
  freq,
  to = null,
  type = "sine",
  duration = 0.3,
  gain = 0.3,
  attack = 0.006,
}) {
  const audio = open();
  if (!audio) return;

  const start = audio.currentTime + at;
  const osc = audio.createOscillator();
  const level = audio.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (to) osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), start + duration);

  level.gain.setValueAtTime(0.0001, start);
  level.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain * accent), start + attack);
  level.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  osc.connect(level);
  level.connect(master);
  osc.start(start);
  osc.stop(start + duration + 0.05);
}

// Ruis door een filter van from naar to; korte attack is een klap, lange een aanzwelling.
export function sweep({
  at = 0,
  duration = 0.4,
  gain = 0.2,
  from = 6000,
  to = 300,
  type = "lowpass",
  q = 1,
  attack = 0.02,
}) {
  const audio = open();
  if (!audio) return;

  const buffer = noise();
  if (!buffer) return;

  const start = audio.currentTime + at;
  const source = audio.createBufferSource();
  const filter = audio.createBiquadFilter();
  const level = audio.createGain();

  source.buffer = buffer;
  source.loop = true;

  filter.type = type;
  filter.Q.value = q;
  filter.frequency.setValueAtTime(from, start);
  filter.frequency.exponentialRampToValueAtTime(Math.max(40, to), start + duration);

  level.gain.setValueAtTime(0.0001, start);
  level.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain * accent), start + attack);
  level.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  source.connect(filter);
  filter.connect(level);
  level.connect(master);
  source.start(start);
  source.stop(start + duration + 0.05);
}

// Wat er nu uit een bestand speelt, zodat stopSamples het kan afbreken.
const playing = new Set();

// Een opgehaald bestand afspelen (zie samples.js). fadeIn/fadeOut in seconden, voor lange
// opnames die anders met een klap beginnen of midden in een maat ophouden. skip slaat het
// begin over, voor een gedownload bestand met stilte vooraan.
export function playBuffer(
  buffer,
  { at = 0, gain = 1, fadeIn = 0, fadeOut = 0, skip = 0 } = {},
) {
  const audio = open();
  if (!audio || !buffer) return false;

  const source = audio.createBufferSource();
  const level = audio.createGain();
  const start = audio.currentTime + at;
  const end = start + buffer.duration - skip;
  source.buffer = buffer;

  // Infade vanaf -40 dB: vanaf -80 dB is de eerste helft onhoorbaar en voelt de fade korter.
  if (fadeIn > 0) {
    level.gain.setValueAtTime(Math.max(0.0001, gain * 0.01), start);
    level.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain), start + fadeIn);
  } else {
    level.gain.setValueAtTime(gain, start);
  }

  // Uitfade tot het eind, maar nooit voordat de infade klaar is.
  if (fadeOut > 0) {
    const from = Math.max(start + fadeIn, end - fadeOut);
    level.gain.setValueAtTime(Math.max(0.0001, gain), from);
    level.gain.exponentialRampToValueAtTime(0.0001, end);
  }

  source.connect(level);
  level.connect(master);
  source.start(start, skip);

  const entry = { source, level };
  playing.add(entry);
  source.onended = () => playing.delete(entry);
  return true;
}

// Alles uit bestanden afbreken, met een korte fade zodat het niet klikt.
export function stopSamples({ fade = 0.2 } = {}) {
  if (!ctx || playing.size === 0) return;

  const now = ctx.currentTime;
  playing.forEach(({ source, level }) => {
    try {
      level.gain.cancelScheduledValues(now);
      level.gain.setValueAtTime(Math.max(0.0001, level.gain.value), now);
      level.gain.exponentialRampToValueAtTime(0.0001, now + fade);
      source.stop(now + fade + 0.02);
    } catch {
      // Was al afgelopen.
    }
  });
  playing.clear();
}

// Een opgehaald bestand decoderen; null als dat niet lukt, ook als het bestand ontbreekt.
export async function decodeAudio(data) {
  const audio = context();
  if (!audio) return null;
  try {
    return await audio.decodeAudioData(data);
  } catch {
    return null;
  }
}

// Een klokje: grondtoon plus een zachtere, net ontstemde boventoon.
export function bell({ at = 0, freq, duration = 0.9, gain = 0.22 }) {
  tone({ at, freq, duration, gain });
  tone({ at, freq: freq * 2.01, duration: duration * 0.55, gain: gain * 0.35 });
}
